import React, { useState } from 'react';
import { Upload, message, Button, Card, Space, Divider, Alert } from 'antd';
import { UploadOutlined, FileOutlined } from '@ant-design/icons';
import { 
  useUploadFacturesDBF, 
  useUploadFacturesDetailsDBF, 
  useUploadBothFacturesDBF 
} from '../../hooks/factureHooks';

const BulkUploadFactures = () => {
  const [facturesFile, setFacturesFile] = useState(null);
  const [detailsFile, setDetailsFile] = useState(null);
  
  const { handleUpload: uploadFactures, loading: loadingFactures, error: errorFactures, uploadResult: resultFactures } = useUploadFacturesDBF();
  const { handleUpload: uploadDetails, loading: loadingDetails, error: errorDetails, uploadResult: resultDetails } = useUploadFacturesDetailsDBF();
  const { handleUpload: uploadBoth, loading: loadingBoth, error: errorBoth, uploadResult: resultBoth } = useUploadBothFacturesDBF();

  const handleUploadFactures = async () => {
    if (!facturesFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadFactures(facturesFile);
      message.success('Factures uploaded successfully!');
      setFacturesFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUploadDetails = async () => {
    if (!detailsFile) {
      message.error('Please select a DBF file first!');
      return;
    }

    try {
      await uploadDetails(detailsFile);
      message.success('DetailFactures uploaded successfully!');
      setDetailsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUploadBoth = async () => {
    if (!facturesFile || !detailsFile) {
      message.error('Please select both DBF files!');
      return;
    }

    try {
      await uploadBoth(facturesFile, detailsFile);
      message.success('Both files uploaded successfully!');
      setFacturesFile(null);
      setDetailsFile(null);
    } catch (err) {
      message.error(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const facturesProps = {
    onRemove: () => {
      setFacturesFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      // Check if file has .dbf extension (case-insensitive)
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setFacturesFile(file);
      return false;
    },
    fileList: facturesFile ? [facturesFile] : [],
    accept: '.dbf,.DBF',
  };

  const detailsProps = {
    onRemove: () => {
      setDetailsFile(null);
    },
    beforeUpload: (file) => {
      const fileName = file.name.toLowerCase();
      // Check if file has .dbf extension (case-insensitive)
      if (!fileName.endsWith('.dbf')) {
        message.error('Please upload a DBF file (.dbf or .DBF)!');
        return Upload.LIST_IGNORE;
      }
      setDetailsFile(file);
      return false;
    },
    fileList: detailsFile ? [detailsFile] : [],
    accept: '.dbf,.DBF',
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Upload Factures DBF Files" style={{ marginBottom: 24 }}>
        <Alert
          message="DBF File Upload Instructions"
          description={
            <div>
              <p><strong>Upload Factures Header file (.dbf):</strong> Contains the main facture records (typically FACVEN.dbf)</p>
              <p><strong>Upload Factures Detail file (.dbf):</strong> Contains the detail/line items for factures (typically DETFACVEN.dbf)</p>
              <p><strong>Upload Both:</strong> Upload both files together (recommended)</p>
              <p style={{ marginTop: 8, color: '#52c41a' }}>
                <strong>Note:</strong> Any file name with .dbf or .DBF extension is accepted
              </p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Upload FACVEN.dbf */}
          <Card size="small" title="1. Upload Factures Headers (.dbf)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...facturesProps}>
                <Button icon={<UploadOutlined />} disabled={loadingFactures || loadingBoth}>
                  Select DBF File (Headers)
                </Button>
              </Upload>
              {facturesFile && (
                <div style={{ marginTop: 8 }}>
                  <FileOutlined /> {facturesFile.name}
                </div>
              )}
              <Button
                type="primary"
                onClick={handleUploadFactures}
                disabled={!facturesFile || loadingFactures || loadingBoth}
                loading={loadingFactures}
              >
                {loadingFactures ? 'Uploading...' : 'Upload Factures'}
              </Button>
              {errorFactures && (
                <Alert message={errorFactures} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultFactures && (
                <Alert
                  message="Upload Successful"
                  description={resultFactures.message || 'Factures seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>

          {/* Upload DETFACVEN.dbf */}
          <Card size="small" title="2. Upload Factures Details (.dbf)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Upload {...detailsProps}>
                <Button icon={<UploadOutlined />} disabled={loadingDetails || loadingBoth}>
                  Select DBF File (Details)
                </Button>
              </Upload>
              {detailsFile && (
                <div style={{ marginTop: 8 }}>
                  <FileOutlined /> {detailsFile.name}
                </div>
              )}
              <Button
                type="primary"
                onClick={handleUploadDetails}
                disabled={!detailsFile || loadingDetails || loadingBoth}
                loading={loadingDetails}
              >
                {loadingDetails ? 'Uploading...' : 'Upload Details'}
              </Button>
              {errorDetails && (
                <Alert message={errorDetails} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultDetails && (
                <Alert
                  message="Upload Successful"
                  description={resultDetails.message || 'DetailFactures seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>

          <Divider>OR</Divider>

          {/* Upload Both */}
          <Card size="small" title="3. Upload Both Files Together (Recommended)" bordered>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Upload {...facturesProps}>
                  <Button icon={<UploadOutlined />} disabled={loadingBoth}>
                    Select Headers File (.dbf)
                  </Button>
                </Upload>
                {facturesFile && (
                  <span style={{ marginLeft: 8 }}>
                    <FileOutlined /> {facturesFile.name}
                  </span>
                )}
              </div>
              <div>
                <Upload {...detailsProps}>
                  <Button icon={<UploadOutlined />} disabled={loadingBoth}>
                    Select Details File (.dbf)
                  </Button>
                </Upload>
                {detailsFile && (
                  <span style={{ marginLeft: 8 }}>
                    <FileOutlined /> {detailsFile.name}
                  </span>
                )}
              </div>
              <Button
                type="primary"
                size="large"
                onClick={handleUploadBoth}
                disabled={!facturesFile || !detailsFile || loadingBoth}
                loading={loadingBoth}
                style={{ marginTop: 16 }}
              >
                {loadingBoth ? 'Uploading Both Files...' : 'Upload Both Files'}
              </Button>
              {errorBoth && (
                <Alert message={errorBoth} type="error" showIcon style={{ marginTop: 8 }} />
              )}
              {resultBoth && (
                <Alert
                  message="Upload Successful"
                  description={resultBoth.message || 'Both files seeded successfully'}
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
            </Space>
          </Card>
        </Space>
      </Card>
    </div>
  );
};

export default BulkUploadFactures;





